// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Compact health-note markup: the plain-language note + cue strings shown on
// the dashboard health tiles, and the detection-window selection helpers that
// drive tile/chart highlighting.
//
// - compact*Note / compactCylinderCue / compactTileNoteText / tileNoteMarkup:
//   per-metric one-line notes (missing-channel fallbacks, CHT/cooling/turbo/
//   oil-system phrasings) sized for the compact tile footprint.
// - compactHealthNote: the per-metric dispatcher the renderers consume.
// - healthCardNoteMarkup: note + linked evidence markup for a health card.
// - detectionWindowMatchesCylinder / detectionWindowsForHealthKey: thin
//   wrappers over src/findings/detection-windows.js that inject live app
//   context (selected cylinder, engine descriptors, sensor summaries).
// - selectedDetectionWindows / playbackDetectionWindows: memoized window
//   selections for the active health key and for playback annunciations. The
//   cache objects stay in app.js (other factories invalidate them through
//   app.js setter lambdas); this module reads/replaces them via deps.
//
// All shared state is injected via getters; nothing here is module-owned.

import { escapeHtml } from "../core/html.js";
import { compactRange, panelNumber } from "../core/display.js";
import {
  engineScopedChannelLabel,
  engineScopedCylinderLabel,
  engineScopedProbeLabel,
} from "../core/engines.js";
import {
  detectionWindowMatchesCylinder as detectionWindowMatchesCylinderFromContext,
  detectionWindowsForHealthKey as detectionWindowsForHealthKeyFromContext,
  divergenceEventsAboveVisibilityThreshold,
} from "../findings/detection-windows.js";
import { healthMetricLabels as summaryHealthMetricLabels } from "../analysis/summary.js";

export function createHealthNoteMarkup({
  currentEngineDescriptors,
  sensorChannelList,
  sensorPrimarySummaries,
  sensorSummaryDisplayLabel,
  readableProbeLabel,
  findingCylinderReference,
  explorerEgtAnnunciationLabel,
  reviewMonitorEnabled,
  getHealthSummary,
  getRenderedHealthSummary,
  getSelectedHealthKey,
  getSelectedCylinderNumber,
  getSelectedOperatingContextWindows,
  getSelectedFlight,
  getSelectedDetectionWindowCache,
  setSelectedDetectionWindowCache,
  getPlaybackDetectionWindowCache,
  setPlaybackDetectionWindowCache,
}) {
function compactMissingHealthNote(key, note = "") {
  if (key === "sensors") return "No sensor channels";
  if (key === "oilSystem") {
    if (/no oil temperature or pressure channels|no oil channel/i.test(note)) return "No oil channel";
    if (/running engine/i.test(note)) return "Need running oil data";
    return note || "No oil data";
  }
  if (key === "coolantMargin") {
    if (/no coolant temperature channel/i.test(note)) return "No coolant channel";
    if (/running engine/i.test(note)) return "Need running coolant data";
    return note || "No coolant data";
  }
  if (key === "cht") return "No CHT data";
  if (key === "chtRhythm") return /stable/i.test(note) ? "Need stable window" : "Need CHT channels";
  if (key === "egt") return "No EGT spread";
  if (key === "valve") {
    if (/steady-power|stable|cruise/i.test(note)) return "Need steady-power segment";
    if (/sample/i.test(note)) return "Need EGT samples";
    return "Need EGT channels";
  }
  if (key === "combustion") return /steady-power|stable|cruise/i.test(note) ? "Need steady-power segment" : "Need CHT/EGT data";
  if (key === "divergence") return /samples/i.test(note) ? "Need run samples" : "Need EGT/CHT data";
  if (key === "descentEgtDropout") return /samples/i.test(note) ? "Need descent samples" : "Need EGT and power data";
  if (key === "inductionLeak") return /samples/i.test(note) ? "Need induction samples" : "Need RPM/fuel/EGT data";
  if (key === "mag") {
    if (/rpm/i.test(note)) return "Need RPM channel";
    if (/inconclusive/i.test(note)) return "Check inconclusive";
    return "No mag check found";
  }
  if (key === "carbIce") return /samples/i.test(note) ? "Need carb-temp samples" : "No carb-temp channel";
  if (key === "cooling") return /samples/i.test(note) ? "Need CHT samples" : "Need CHT channels";
  if (key === "coolingRate") return /samples/i.test(note) ? "Need CHT samples" : "Need CHT channel";
  if (key === "charging") return /start/i.test(note) ? "No start found" : "Need bus voltage";
  if (key === "electrical") return "No bus voltage";
  return note || "No data";
}

function compactProbeLabelForKey(key, fallback = "") {
  if (!key) return fallback;
  const descriptors = currentEngineDescriptors();
  return engineScopedProbeLabel(key, descriptors)
    || engineScopedChannelLabel(key, descriptors)
    || readableProbeLabel(key)
    || fallback;
}

function compactCylinderLabel(cylinder) {
  if (!Number.isFinite(cylinder)) return "";
  return engineScopedCylinderLabel(cylinder, currentEngineDescriptors()) || findingCylinderReference(cylinder).label;
}

function compactChtHealthNote(metric) {
  const details = metric?.details ?? {};
  const event = details.severityFloor;
  if (event) {
    const label = compactProbeLabelForKey(event.channelKey, "CHT");
    return `${label} ${event.thresholdF} F+ for ${Math.round(event.durationSeconds)} s`;
  }
  const hottest = details.peak ? { key: details.peak.key, cylinder: details.peak.cylinder, max: details.peak.value } : details.hottest ?? {};
  const peak = Number.isFinite(hottest.max) ? hottest.max : details.max;
  if (!Number.isFinite(peak)) return metric?.score >= 88 ? "CHT in range" : "CHT review cue";
  const label = compactProbeLabelForKey(hottest.key, compactCylinderLabel(hottest.cylinder) || "CHT");
  return label ? `${label} peak ${Math.round(peak)} F` : `Peak ${Math.round(peak)} F`;
}

function compactCoolingNote(metric) {
  const top = metric?.details?.top ?? {};
  const cylinderLabel = compactCylinderLabel(top.cylinder);
  if (metric?.score >= 88) {
    return cylinderLabel ? `${cylinderLabel} cooling even` : "Cooling even";
  }
  if (cylinderLabel && Number.isFinite(top.p95Delta)) {
    return `${cylinderLabel} +${Math.round(top.p95Delta)} F vs peers`;
  }
  return cylinderLabel ? `${cylinderLabel} cooling cue` : "Cooling cue";
}

function compactCoolingRateNote(metric) {
  const top = metric?.details?.top ?? {};
  if (Number.isFinite(top.rateFpm)) {
    const label = compactProbeLabelForKey(top.key, compactCylinderLabel(top.cylinder) || "CHT");
    return `${label || "CHT"} ${Math.round(top.rateFpm)} F/min`;
  }
  const threshold = Number.isFinite(Number(metric?.details?.thresholdFpm)) ? Number(metric.details.thresholdFpm) : 50;
  return metric?.score >= 88 ? `Rate below ${Math.round(threshold)} F/min` : "Cooling-rate cue";
}

function compactCylinderCue(prefix, metric, fallback) {
  const top = metric?.details?.top ?? {};
  const cylinder = Number.isFinite(top.cylinder)
    ? top.cylinder
    : Array.isArray(top.cylinders) && Number.isFinite(top.cylinders[0])
      ? top.cylinders[0]
      : null;
  const label = compactCylinderLabel(cylinder);
  return label ? `${prefix} ${label}` : fallback;
}

function compactTileNoteText(value, maxLength = 52) {
  const text = String(value ?? "").replace(/\s+/g, " ").trim();
  if (text.length <= maxLength) return text;
  const sliced = text.slice(0, maxLength + 1);
  const boundary = Math.max(sliced.lastIndexOf(" "), sliced.lastIndexOf(";"), sliced.lastIndexOf(","));
  const head = boundary >= Math.floor(maxLength * 0.62) ? sliced.slice(0, boundary) : text.slice(0, maxLength);
  return `${head.trim().replace(/[;,.:-]+$/, "")}...`;
}

function tileNoteSpan(value, maxLength) {
  return `<span>${escapeHtml(compactTileNoteText(value, maxLength))}</span>`;
}

function tileNoteMarkup(value, maxLength) {
  return escapeHtml(compactTileNoteText(value, maxLength));
}

function compactTurboHealthNote(metric) {
  const details = metric?.details ?? {};
  const status = details.status;
  if (status === "needs-profile-limits") return "Profile limits needed";
  if (status === "data-quality-review") {
    const label = details.titQuality?.label ?? "TIT";
    return `${label} signal cue`;
  }
  if (status === "limit-review") {
    const limitTypes = new Set((details.eventWindows ?? [])
      .filter((window) => window?.kind !== "jumps" && !/signal quality/i.test(window?.label ?? ""))
      .map((window) => {
        if (window.key === "map" || /\bMAP\b/i.test(window.label ?? "")) return "MAP";
        if (window.key === "tit" || /\bTIT\b/i.test(window.label ?? "")) return "TIT";
        if (window.key === "compressorDischargeTemp") return "CDT";
        if (window.key === "inductionTemp") return "Induction temp";
        return "";
      })
      .filter(Boolean));
    const labels = [...limitTypes];
    if (labels.length === 1) return `${labels[0]} limit cue`;
    if (labels.length === 2) return `${labels.join(" + ")} limit cues`;
    if (labels.length > 2) return `${labels.length} limit cues`;
    return "Turbo limit cue";
  }
  if (status === "heat-margin-review") return "Heat-margin cue";
  if (status === "heat-margin-watch") return "Heat-margin watch";
  if (status === "margin-watch") return "TIT near margin";
  if (metric?.score >= 88) return "Within limits";
  return "Turbo cue";
}

function compactHealthNote(key, metric, options = {}) {
  if (!metric) return "";
  const details = metric.details ?? {};
  if (!Number.isFinite(metric.score)) return compactMissingHealthNote(key, metric.note);

  if (key === "sensors") {
    const reviewScore = Number.isFinite(metric.rawScore) ? metric.rawScore : metric.score;
    const sharedRef = details.sharedReferenceCue;
    if (sharedRef) {
      const egt = sharedRef.flaggedEgtCount ?? 0;
      const cht = sharedRef.flaggedChtCount ?? 0;
      const tit = sharedRef.flaggedTitCount ?? 0;
      const counts = [egt && `${egt} EGT`, cht && `${cht} CHT`, tit && `${tit} TIT`].filter(Boolean).join(" + ");
      return counts ? `Shared probe reference · ${counts}` : "Shared probe reference pattern";
    }
    if (reviewScore >= 88) return "Channels stable";
    const noiseTop = details.top;
    if (noiseTop?.noiseSuspicion > 0 && noiseTop.conditionWindow?.kind === "noise") {
      const count = noiseTop.noiseAffectedCount ?? 1;
      const bank = noiseTop.type === "egt" ? "EGT" : noiseTop.type === "cht" ? "CHT" : sensorSummaryDisplayLabel(noiseTop);
      return count > 1 ? `${bank} bank noisy · ${count} probes` : `${sensorSummaryDisplayLabel(noiseTop)} noisy`;
    }
    const primarySummaries = sensorPrimarySummaries(metric, 3);
    const labels = primarySummaries
      .map((summary) => sensorSummaryDisplayLabel(summary))
      .filter(Boolean);
    if (labels.length) return `${sensorChannelList(labels)} probe cue`;
    const top = details.top ?? {};
    const topLabel = sensorSummaryDisplayLabel(top);
    return topLabel ? `${topLabel} probe cue` : "Probe cue";
  }

  if (key === "turbo") return compactTurboHealthNote(metric);
  if (key === "oilSystem") {
    const issue = details.top?.issue;
    if (issue) return metric.note || `${issue.label}: ${issue.detail}`;
    if (details.pressureLimitsKnown === false) return "Generic screen; pressure limits incomplete";
    const pressure = (details.summaries ?? []).find((summary) => summary.kind === "pressure");
    const temp = (details.summaries ?? []).find((summary) => summary.kind === "temperature");
    const parts = [
      pressure ? `P ${panelNumber(pressure.min, " psi")} min` : "",
      temp ? `T ${panelNumber(temp.max, " F")} max` : "",
    ].filter(Boolean);
    return parts.length ? parts.join("; ") : "Oil in range";
  }
  if (key === "coolantMargin") {
    const issue = details.top?.issue;
    if (issue) return metric.note || `${issue.label}: ${issue.detail}`;
    if (Number.isFinite(details.max) && Number.isFinite(details.marginF)) {
      return `Max ${panelNumber(details.max, " F")} (${panelNumber(details.marginF, " F")} margin)`;
    }
    return metric.score >= 88 ? "Coolant margin clear" : "Coolant margin cue";
  }

  if (key === "cht") return compactChtHealthNote(metric);
  if (key === "chtRhythm") return metric.score >= 88 ? "No CHT rhythm cue" : compactCylinderCue("CHT rhythm on", metric, "CHT rhythm cue");
  if (key === "egt") return metric.score >= 88 ? "EGTs balanced" : "Large EGT difference";
  if (key === "valve") {
    const isRotax = options.summary?.applicability?.engineFamily === "rotax"
      || metric.details?.rhythmProfile?.engineFamily === "rotax";
    if (metric.score >= 88) return isRotax ? "No EGT variability detected" : "No rhythm cue";
    return isRotax
      ? compactCylinderCue("Variability on", metric, "EGT variability")
      : compactCylinderCue("Rhythm cue on", metric, "Rhythm cue");
  }
  if (key === "combustion") return metric.score >= 88 ? "Pairing clear" : details.top?.label ?? compactCylinderCue("Pairing cue on", metric, "Pairing cue");
  if (key === "abnormalCombustion") {
    if (details.egtBalanceCue) {
      const label = details.egtBalanceCue.label || "EGT";
      return `${label} drop; no CHT cue`;
    }
    return metric.score >= 88 ? "No combustion cue" : compactCylinderCue("Combustion cue on", metric, "Combustion cue");
  }
  if (key === "divergence") return metric.score >= 88 ? (divergenceEventsAboveVisibilityThreshold(metric).length ? compactCylinderCue("Split observed on", metric, "Split observed") : "No split cue") : compactCylinderCue("Split event on", metric, "Split event");
  if (key === "descentEgtDropout") return metric.score >= 88 ? "No descent dropout" : compactCylinderCue("EGT dropout on", metric, "EGT dropout");
  if (key === "inductionLeak") {
    if (metric.score >= 88) {
      return (details.separatedEventCount ?? 0) > 0 ? "Single cue, not repeated" : "No induction-leak cue";
    }
    if (Array.isArray(details.engines) && details.engines.length > 1) {
      const flagged = details.engines.filter((entry) =>
        Number.isFinite(entry?.score) && entry.score < 88 && Number.isFinite(entry?.details?.top?.cylinder));
      if (flagged.length >= 2) {
        const refs = flagged.map((entry) => findingCylinderReference(entry.details.top.cylinder).label);
        return `Induction-leak cues on ${refs.join(", ")}`;
      }
    }
    const cylinder = details.top?.cylinder;
    const cylinderLabel = Number.isFinite(cylinder) ? ` on ${findingCylinderReference(cylinder).label}` : "";
    return `Induction-leak cue${cylinderLabel}`;
  }
  if (key === "mag") return metric.score >= 88 ? "Mag check identified" : metric.note?.replace(/\bscreen\b/i, "check") ?? "Mag check cue";
  if (key === "carbIce") return metric.score >= 88 ? "No carb-ice cue" : "Carb-ice cue";
  if (key === "cooling") return compactCoolingNote(metric);
  if (key === "coolingRate") return compactCoolingRateNote(metric);
  if (key === "charging") return details.status === "pass" ? "Recovered" : metric.note ?? "Start not confirmed";
  if (key === "electrical") {
    const range = compactRange(details.runningMin, details.runningMax, " V", 1);
    return range ? `Bus ${range}` : metric.note ?? "Electrical check";
  }
  if (key === "instrumentationSuspicion") {
    if (metric.score >= 88) return "Probes coherent";
    const egt = details.flaggedEgtCount ?? 0;
    const cht = details.flaggedChtCount ?? 0;
    if (egt && cht) return `${egt} EGT + ${cht} CHT flagged`;
    return "Shared probe reference cue";
  }

  return metric.note ?? "";
}

function compactOilSystemTileMarkup(metric) {
  const issue = metric?.details?.top?.issue;
  if (!issue) return "";
  const value = Number.isFinite(issue.value) ? issue.value : null;
  const unit = issue.unit ?? " F";
  const valueLabel = value === null
    ? ""
    : issue.label.toLowerCase().includes("below operating")
      ? `Peak ${panelNumber(value, unit)}`
      : `${issue.label.toLowerCase().includes("high") ? "Max" : "Min"} ${panelNumber(value, unit)}`;
  return `${tileNoteSpan(issue.label)}${valueLabel ? tileNoteSpan(valueLabel) : ""}`;
}

function compactCoolantMarginTileMarkup(metric) {
  const issue = metric?.details?.top?.issue;
  if (!issue) return "";
  const value = Number.isFinite(issue.value) ? issue.value : metric?.details?.max;
  const valueLabel = Number.isFinite(value) ? `Max ${panelNumber(value, " F")}` : "";
  return `${tileNoteSpan(issue.label)}${valueLabel ? tileNoteSpan(valueLabel) : ""}`;
}

function healthCardNoteMarkup(key, metric, options = {}) {
  if (!metric) return "";
  const details = metric.details ?? {};
  if (key === "oilSystem") {
    const oilMarkup = compactOilSystemTileMarkup(metric);
    if (oilMarkup) return oilMarkup;
  }
  if (key === "coolantMargin") {
    const coolantMarkup = compactCoolantMarginTileMarkup(metric);
    if (coolantMarkup) return coolantMarkup;
  }
  if (key === "egt" && details.hot?.isReview) {
    const hot = details.hot;
    const elevatedCount = hot.elevatedChannels?.length ?? 0;
    if (elevatedCount > 1) {
      return `${tileNoteSpan("High absolute EGTs")}${tileNoteSpan(`${elevatedCount.toLocaleString()} channels above expected; peak ${Math.round(hot.max)} F`)}`;
    }
    const hotLabel = readableProbeLabel(hot.key) || readableProbeLabel(hot.label) || hot.label || "EGT";
    return `${tileNoteSpan("High absolute EGT")}${tileNoteSpan(`${hotLabel} reached ${Math.round(hot.max)} F`)}`;
  }
  if (key === "egt" && Number.isFinite(metric.score) && Number.isFinite(details.p90)) {
    if (details.sustainedLowEgt) {
      const sustainedLow = details.sustainedLowEgt;
      const cylinderLabel = compactCylinderLabel(sustainedLow.cylinder) || sustainedLow.label || "EGT";
      const headline = sustainedLow.strength === "strong" ? "Sustained EGT drop" : "Sustained low EGT";
      const deviation = Number.isFinite(sustainedLow.p90DeviationF)
        ? `${Math.round(sustainedLow.p90DeviationF)} F below peers`
        : "below peers";
      return `${tileNoteSpan(`${headline} on ${cylinderLabel}`)}${tileNoteSpan(deviation)}`;
    }
    if (Array.isArray(details.engines) && details.engines.length > 1) {
      const engineSegments = details.engines.map((entry) => {
        const enginP90 = entry?.details?.p90;
        if (!Number.isFinite(enginP90)) return null;
        const label = entry.shortLabel ?? entry.label ?? "";
        return `${label} ${Math.round(enginP90)} F`.trim();
      }).filter(Boolean);
      if (engineSegments.length) {
        return `${tileNoteSpan("Large EGT difference")}${tileNoteSpan(`${engineSegments.join(" · ")} between cylinders`)}`;
      }
    }
    return `${tileNoteSpan("Large EGT difference")}${tileNoteSpan(`${Math.round(details.p90)} F between cylinders`)}`;
  }
  return tileNoteMarkup(compactHealthNote(key, metric, options));
}

function detectionWindowMatchesCylinder(window, cylinderNumber = getSelectedCylinderNumber()) {
  return detectionWindowMatchesCylinderFromContext(window, cylinderNumber);
}

function detectionWindowsForHealthKey(key, metric) {
  return detectionWindowsForHealthKeyFromContext(key, metric, {
    findingCylinderReference,
    readableProbeLabel,
    explorerEgtAnnunciationLabel,
  });
}

function selectedDetectionWindows(summary = getRenderedHealthSummary() ?? getHealthSummary()) {
  if (getSelectedOperatingContextWindows().length) return getSelectedOperatingContextWindows();
  if (!summary || !getSelectedHealthKey()) return [];
  if (!reviewMonitorEnabled(getSelectedHealthKey(), getSelectedFlight()?.aircraft)) return [];
  const key = `${getSelectedHealthKey()}|${getSelectedCylinderNumber() ?? ""}`;
  if (getSelectedDetectionWindowCache().summary === summary && getSelectedDetectionWindowCache().key === key) {
    return getSelectedDetectionWindowCache().windows;
  }
  const windows = detectionWindowsForHealthKey(getSelectedHealthKey(), summary[getSelectedHealthKey()])
    .filter((window) => detectionWindowMatchesCylinder(window));
  setSelectedDetectionWindowCache({ summary, key, windows });
  return windows;
}

function playbackDetectionWindows(summary = getHealthSummary()) {
  if (!summary) return [];
  if (getPlaybackDetectionWindowCache().summary === summary) return getPlaybackDetectionWindowCache().windows;
  const windows = Object.keys(summaryHealthMetricLabels)
    .filter((key) => reviewMonitorEnabled(key, getSelectedFlight()?.aircraft))
    .flatMap((key) => detectionWindowsForHealthKey(key, summary[key])
      .map((window) => ({
        ...window,
        key: window.key ?? key,
        sourceKey: key,
        sourceLabel: summaryHealthMetricLabels[key],
      })))
    .filter((window) => Number.isFinite(window.startTime))
    .sort((a, b) => a.startTime - b.startTime);
  setPlaybackDetectionWindowCache({ summary, windows });
  return windows;
}

  return {
    compactMissingHealthNote,
    compactCylinderLabel,
    compactHealthNote,
    healthCardNoteMarkup,
    detectionWindowsForHealthKey,
    selectedDetectionWindows,
    playbackDetectionWindows,
  };
}
